/**
 * Funções PURAS de apresentação da "Atualização do Instrumento" (ex-Migração de
 * Catálogo). Sem rede, sem estado: tradução técnico→leigo, mapeamento das 3 ações
 * do avaliador, resumo em português natural e a Conferência de Segurança.
 *
 * Adaptação à API real: o backend devolve `decisao` como
 * `pendente|migrar_texto|migrar_recode|manter|rejeitar` e `codigoDestino: string|null`.
 * A UI fala em `aceitar|manter|descartar` e converte com `acaoParaEfeito` na hora
 * de chamar `api.decidir`. Os códigos só servem ao mapeamento e ao modo técnico.
 *
 * NÃO reimplementa `validarPreflight` (fonte da verdade no backend): a conferência
 * compõe um checklist amigável a partir dos itens e MESCLA os bloqueios/avisos
 * reais do pré-flight (READ-ONLY) — ver `mesclarPreflight`.
 */
import { SETORES } from "@caint/data";
import type {
  ConflitoRemoto,
  DecisaoMigracaoValor,
  ItemMigracaoRemoto,
  PreflightRemoto,
  ResultadoExecucaoRemoto,
} from "../../../lib/api.ts";

/** Abaixo deste limiar (Jaccard), a redação é "muito diferente" — provável item
 * distinto, não só nova redação (espelha o aviso do pré-flight). */
const LIMIAR_SIMILARIDADE = 0.45;

// ── Vocabulário (estado, setor, fases) ───────────────────────────────────────

const LABEL_ESTADO: Record<string, { texto: string; cor: string }> = {
  em_revisao: { texto: "Em revisão pelos avaliadores", cor: "var(--slate-600)" },
  aguardando_aprovacao: { texto: "Aguardando aprovação", cor: "var(--teal-600)" },
  aprovado: { texto: "Pronta para aplicar", cor: "var(--teal-600)" },
  concluido: { texto: "Aplicada", cor: "var(--success)" },
  revertido: { texto: "Desfeita", cor: "var(--coral-500)" },
  cancelado: { texto: "Cancelada", cor: "var(--coral-500)" },
};

export function rotuloEstado(estado: string): { texto: string; cor: string } {
  return LABEL_ESTADO[estado] ?? { texto: estado, cor: "var(--slate-600)" };
}

const NOME_SETOR = new Map(SETORES.map((s) => [s.code, s.nome]));
const MACRO_SETOR = new Map(SETORES.map((s) => [s.code, s.macro]));

/** Cor do ponto de setor por macroprocesso (A/D/G/L/S) — identifica a área sem
 * expor a sigla técnica. Tokens do DS CAINT. */
const COR_MACRO: Record<string, string> = {
  G: "#585FA0", // Governança (periwinkle)
  A: "#2D74B8", // Assistência (azul)
  S: "#D06B7C", // Segurança (rosa)
  D: "#2E9E6B", // Diagnóstico/apoio (verde)
  L: "#E3A317", // Logística (âmbar)
};

export function nomeSetor(code: string): string {
  return NOME_SETOR.get(code) ?? code;
}

export function corSetor(code: string): string {
  return COR_MACRO[MACRO_SETOR.get(code) ?? ""] ?? "var(--slate-500)";
}

/** Identifica o item pela pergunta + setor — nunca pelo código interno. */
export function identificarItem(item: {
  perguntaAtual: string | null;
  perguntaNova: string | null;
  setor: string;
}): string {
  const pergunta = item.perguntaAtual ?? item.perguntaNova ?? "Item sem descrição";
  return `“${pergunta}” · ${nomeSetor(item.setor)}`;
}

export const FASES = [
  { titulo: "Revisão dos itens", desc: "avaliadores" },
  { titulo: "Conferência", desc: "presidência" },
  { titulo: "Aplicação", desc: "mudanças entram" },
  { titulo: "Publicação", desc: "equipe de TI" },
] as const;

/** Índice da fase ativa no stepper a partir do estado do lote. */
export function faseDoEstado(estado: string): number {
  switch (estado) {
    case "em_revisao":
    case "aguardando_aprovacao":
      return 0;
    case "aprovado":
    case "revertido":
      return 1;
    case "concluido":
      return 3; // aplicado; a publicação (fase 4) é externa, pela TI
    default:
      return 0;
  }
}

// ── Ações do avaliador (3 leigas → efeito técnico) ───────────────────────────

export type AcaoAvaliador = "aceitar" | "manter" | "descartar";
export type Canal = "texto" | "recode";

export interface DecisaoTecnica {
  decisao: DecisaoMigracaoValor;
  codigoDestino: string | null;
}

/** Canal interno (escondido): código muda ⇒ recode (transfere avaliações);
 * mesmo código ou sem destino ⇒ só texto. */
export function canal(item: Pick<ItemMigracaoRemoto, "codigoOrigem" | "codigoDestino">): Canal {
  return item.codigoDestino && item.codigoDestino !== item.codigoOrigem ? "recode" : "texto";
}

/** "Aceitar" aplica o tipo já proposto (texto ou recode — escondido do usuário). */
export function acaoParaEfeito(item: ItemMigracaoRemoto, acao: AcaoAvaliador): DecisaoTecnica {
  if (acao === "manter") {
    return { decisao: "manter", codigoDestino: null };
  }
  if (acao === "descartar") {
    return { decisao: "rejeitar", codigoDestino: null };
  }
  return canal(item) === "recode"
    ? { decisao: "migrar_recode", codigoDestino: item.codigoDestino }
    : { decisao: "migrar_texto", codigoDestino: null };
}

/** Ação leiga equivalente à decisão técnica já registrada (echo / agrupamento). */
export function acaoDaDecisao(decisao: string): AcaoAvaliador | null {
  switch (decisao) {
    case "manter":
      return "manter";
    case "rejeitar":
      return "descartar";
    case "migrar_texto":
    case "migrar_recode":
      return "aceitar";
    default:
      return null;
  }
}

/** Confirmação clínica: redação muito diferente E item já avaliado ⇒ provável
 * item DIFERENTE (não só nova redação). `similaridade` nula = tratada como alta. */
export function precisaConfirmacaoClinica(item: ItemMigracaoRemoto): boolean {
  return (item.similaridade ?? 1) < LIMIAR_SIMILARIDADE && item.temAvaliacao;
}

// ── Rótulo do tipo de mudança (chip) ─────────────────────────────────────────

export interface RotuloTipo {
  texto: string;
  tom: "leve" | "medio" | "forte";
  icone: string;
}

export function rotuloTipo(tipoProposto: string): RotuloTipo {
  switch (tipoProposto) {
    case "substituicao":
      return { texto: "Nova redação", tom: "medio", icone: "edit" };
    case "divergente":
      return { texto: "Item renumerado", tom: "forte", icone: "swap" };
    default:
      return { texto: "Pequeno ajuste de texto", tom: "leve", icone: "text" };
  }
}

/** Selo do resultado no card já decidido. */
export function rotuloDecisao(decisao: string): string {
  switch (acaoDaDecisao(decisao)) {
    case "aceitar":
      return "Aceito";
    case "manter":
      return "Mantido";
    case "descartar":
      return "Descartado";
    default:
      return "Sem decisão";
  }
}

// ── Contagens e resumo natural ───────────────────────────────────────────────

export interface Contagem {
  redacao: number;
  renumerar: number;
  manter: number;
  descartar: number;
  pendente: number;
  revisados: number;
  aceitos: number;
  total: number;
  avaliacoes: number;
  evidencias: number;
  itensComAvaliacao: number;
}

export function contar(itens: readonly ItemMigracaoRemoto[]): Contagem {
  const c: Contagem = {
    redacao: 0,
    renumerar: 0,
    manter: 0,
    descartar: 0,
    pendente: 0,
    revisados: 0,
    aceitos: 0,
    total: itens.length,
    avaliacoes: 0,
    evidencias: 0,
    itensComAvaliacao: 0,
  };
  for (const it of itens) {
    switch (it.decisao) {
      case "migrar_texto":
        c.redacao++;
        break;
      case "migrar_recode":
        c.renumerar++;
        break;
      case "manter":
        c.manter++;
        break;
      case "rejeitar":
        c.descartar++;
        break;
      default:
        c.pendente++;
        break;
    }
    if (it.decisao === "migrar_texto" || it.decisao === "migrar_recode") {
      c.avaliacoes += it.nRespostas ?? 0;
      c.evidencias += it.nEvidencias ?? 0;
      if (it.temAvaliacao) {
        c.itensComAvaliacao++;
      }
    }
  }
  c.aceitos = c.redacao + c.renumerar;
  c.revisados = c.aceitos + c.manter + c.descartar;
  return c;
}

function plural(n: number, singular: string, plural_: string): string {
  return `${n} ${n === 1 ? singular : plural_}`;
}

/** Junta partes com vírgula e "e" antes da última ("a, b e c"). */
function listar(partes: readonly string[]): string {
  if (partes.length <= 1) {
    return partes.join("");
  }
  return `${partes.slice(0, -1).join(", ")} e ${partes[partes.length - 1]}`;
}

export interface ResumoNatural {
  frase: string;
  preserv: string;
  contagem: Contagem;
  partes: string[];
}

/** Resumo em português natural do que a atualização vai fazer (presidência). */
export function resumoNatural(itens: readonly ItemMigracaoRemoto[]): ResumoNatural {
  const c = contar(itens);
  const partes: string[] = [];
  if (c.redacao) {
    partes.push(`atualizar a redação de ${plural(c.redacao, "item", "itens")}`);
  }
  if (c.renumerar) {
    partes.push(`renumerar ${plural(c.renumerar, "item", "itens")} (mantendo as avaliações)`);
  }
  if (c.manter) {
    partes.push(`manter ${plural(c.manter, "item", "itens")} como ${c.manter === 1 ? "está" : "estão"}`);
  }
  if (c.descartar) {
    partes.push(`descartar ${plural(c.descartar, "item", "itens")}`);
  }
  const frase = partes.length
    ? `Esta atualização vai ${listar(partes)}.`
    : "Nenhuma mudança selecionada ainda.";
  const preserv = `${plural(c.avaliacoes, "avaliação", "avaliações")} e ${plural(
    c.evidencias,
    "evidência",
    "evidências",
  )} serão preservadas — nenhuma avaliação será perdida.`;
  return { frase, preserv, contagem: c, partes };
}

/** Resumo dos números reais após aplicar (movimentos do servidor). */
export function frasearResultado(res: ResultadoExecucaoRemoto["resumo"]): string[] {
  const totRespostas = res.movimentos.reduce((s, m) => s + m.nRespostas, 0);
  const totEvid = res.movimentos.reduce((s, m) => s + m.nEvidencias, 0);
  const frases: string[] = [];
  if (res.movimentos.length) {
    frases.push(
      `${plural(res.movimentos.length, "item renumerado", "itens renumerados")}, com ${plural(
        totRespostas,
        "avaliação transferida",
        "avaliações transferidas",
      )} e ${plural(totEvid, "evidência", "evidências")} — sem nenhuma perda.`,
    );
  } else {
    frases.push("Nenhum item precisou transferir avaliações.");
  }
  if (res.avaliacoesRevalidadas.length) {
    frases.push(
      `Os índices de qualidade foram recalculados em ${plural(
        res.avaliacoesRevalidadas.length,
        "avaliação",
        "avaliações",
      )}.`,
    );
  }
  return frases;
}

// ── Conferência de Segurança (composição amigável + merge do pré-flight) ─────

export type EstadoCheck = "ok" | "bloqueio" | "atencao";
export interface Check {
  estado: EstadoCheck;
  titulo: string;
  detalhe: string;
}
export interface Conferencia {
  checks: Check[];
  podeAplicar: boolean;
  bloqueios: number;
  atencoes: number;
}

function destinoDuplicado(itens: readonly ItemMigracaoRemoto[]): boolean {
  const cont = new Map<string, number>();
  for (const it of itens) {
    if (it.decisao === "migrar_recode" && it.codigoDestino) {
      cont.set(it.codigoDestino, (cont.get(it.codigoDestino) ?? 0) + 1);
    }
  }
  return [...cont.values()].some((n) => n > 1);
}

function finalizar(checks: Check[]): Conferencia {
  const bloqueios = checks.filter((k) => k.estado === "bloqueio").length;
  const atencoes = checks.filter((k) => k.estado === "atencao").length;
  return { checks, podeAplicar: bloqueios === 0, bloqueios, atencoes };
}

/** Checklist amigável composto a partir dos itens (espelha o pré-flight em
 * linguagem leiga). NÃO é a fonte da verdade — ver `mesclarPreflight`. */
export function conferenciaDeSeguranca(itens: readonly ItemMigracaoRemoto[]): Conferencia {
  const c = contar(itens);
  const checks: Check[] = [];

  // 1. Revisão completa
  if (c.pendente > 0) {
    checks.push({
      estado: "bloqueio",
      titulo: `Há ${plural(c.pendente, "item", "itens")} ainda sem revisão`,
      detalhe: "Peça aos avaliadores para concluir a revisão de todos os itens antes de aplicar.",
    });
  } else {
    checks.push({
      estado: "ok",
      titulo: "Todos os itens foram revisados",
      detalhe: "Cada pergunta recebeu uma decisão.",
    });
  }

  // 2. Destino único
  if (destinoDuplicado(itens)) {
    checks.push({
      estado: "bloqueio",
      titulo: "Dois itens estão sendo unidos no mesmo item novo",
      detalhe: "Revise as decisões: cada item renumerado deve ir para um item novo diferente.",
    });
  } else if (c.renumerar > 0) {
    checks.push({
      estado: "ok",
      titulo: "Cada item renumerado é único",
      detalhe: "Nenhum item novo recebe dois itens antigos.",
    });
  }

  // 3. Sem órfãs (sempre ok)
  checks.push({
    estado: "ok",
    titulo: "Nenhuma avaliação ficará órfã",
    detalhe: `${plural(c.avaliacoes, "avaliação", "avaliações")} serão transferidas com cópia de segurança — é possível desfazer.`,
  });

  // 4. Atenção — nova redação muito diferente de um item já avaliado (mesma
  // pergunta reescrita). Recodes são itens DIFERENTES por construção e já caem em
  // "Itens renumerados" / "Nenhuma avaliação ficará órfã" — não geram esta atenção.
  for (const it of itens) {
    if (acaoDaDecisao(it.decisao) === "aceitar" && canal(it) === "texto" && precisaConfirmacaoClinica(it)) {
      checks.push({
        estado: "atencao",
        titulo: "Mudança grande na redação de um item já avaliado",
        detalhe: `Confira: ${identificarItem(it)}.`,
      });
    }
  }

  // 5. Índices (sempre ok)
  checks.push({
    estado: "ok",
    titulo: "Os índices de qualidade serão recalculados",
    detalhe: "Os índices das unidades afetadas são atualizados automaticamente.",
  });

  return finalizar(checks);
}

// O front já cobre estas duas checagens; evita duplicar ao mesclar o backend.
const COBERTO_PELO_FRONT = (motivo: string) =>
  motivo.startsWith("decisão pendente") ||
  motivo.includes("usado por mais de um item") ||
  motivo.startsWith("texto muito diferente");

/** Mescla os conflitos REAIS do pré-flight (backend) no checklist amigável —
 * garante que nada que só o servidor detecta (catálogo sumiu, destino já existe,
 * avaliação com origem+destino) passe despercebido. */
export function mesclarPreflight(base: Conferencia, pf: PreflightRemoto): Conferencia {
  const extras: Check[] = [];
  for (const b of pf.bloqueios) {
    if (COBERTO_PELO_FRONT(b.motivo)) continue;
    const t = traduzirConflito(b);
    extras.push({ estado: "bloqueio", titulo: t.titulo, detalhe: t.oQueFazer });
  }
  for (const a of pf.avisos) {
    if (COBERTO_PELO_FRONT(a.motivo)) continue;
    const t = traduzirConflito(a);
    extras.push({ estado: "atencao", titulo: t.titulo, detalhe: t.oQueFazer });
  }
  const vistos = new Set(base.checks.map((k) => k.titulo));
  const novos = extras.filter((k) => !vistos.has(k.titulo));
  return finalizar([...base.checks, ...novos]);
}

// ── Dicionário de conflitos crus do pré-flight → leigo ───────────────────────

export interface ConflitoLeigo {
  nivel: "bloqueio" | "aviso";
  titulo: string;
  oQueFazer: string;
}

interface RegraConflito {
  testar: (motivo: string) => boolean;
  titulo: string;
  oQueFazer: string;
}

const REGRAS_CONFLITO: readonly RegraConflito[] = [
  {
    testar: (m) => m.startsWith("decisão pendente"),
    titulo: "Há item sem decisão",
    oQueFazer: "Volte e escolha uma opção (aceitar, manter ou descartar) em cada item antes de aplicar.",
  },
  {
    testar: (m) => m.startsWith("campo não permitido"),
    titulo: "A planilha trouxe um campo que não pode ser alterado",
    oQueFazer:
      "Só os textos (pergunta, processo, norma e evidência) podem mudar. Peça à equipe de TI para revisar a planilha oficial.",
  },
  {
    testar: (m) => m.startsWith("recode sem código destino"),
    titulo: "Falta indicar o item novo de destino",
    oQueFazer: "Esta transferência está sem o item de destino. Reabra a decisão deste item.",
  },
  {
    testar: (m) => m.includes("usado por mais de um item"),
    titulo: "Dois itens vão para o mesmo item novo",
    oQueFazer: "Revise as decisões: cada item antigo deve ir para um item novo diferente.",
  },
  {
    testar: (m) => m.startsWith("código origem não existe mais"),
    titulo: "O instrumento mudou desde que esta atualização começou",
    oQueFazer: "Peça à equipe de TI para recarregar a planilha oficial e iniciar a atualização novamente.",
  },
  {
    testar: (m) => m.includes("já existe no catálogo"),
    titulo: "O item de destino já existe no instrumento",
    oQueFazer: "Esse item novo já está em uso. Peça à equipe de TI para conferir a planilha oficial.",
  },
  {
    testar: (m) => m.startsWith("avaliação já tem resposta"),
    titulo: "Uma unidade já respondeu ao item atual e ao item novo",
    oQueFazer:
      "Transferir juntaria duas respostas da mesma unidade. Peça apoio da equipe de TI antes de aplicar este item.",
  },
  {
    testar: (m) => m.startsWith("recode em código sem avaliações"),
    titulo: "Item novo sem avaliações a transferir",
    oQueFazer: "Tudo certo — não há avaliações a mover; a mudança é só de numeração.",
  },
  {
    testar: (m) => m.startsWith("texto muito diferente"),
    titulo: "A nova redação é bem diferente da atual",
    oQueFazer:
      "Confirme se ainda é a mesma pergunta. Se for outro item, escolha aceitar para transferir as avaliações.",
  },
];

export function traduzirConflito(c: ConflitoRemoto): ConflitoLeigo {
  const regra = REGRAS_CONFLITO.find((r) => r.testar(c.motivo));
  return {
    nivel: c.nivel,
    titulo: regra?.titulo ?? c.motivo,
    oQueFazer: regra?.oQueFazer ?? "Peça apoio da equipe de TI antes de aplicar.",
  };
}
